<script>
import { useAlert } from 'dashboard/composables';
import EditTeimosinhaMessage from './EditMessage.vue';
import EditAction from './EditAction.vue';
import CreateTeimosinhaMessage from './CreateMessage.vue';
import CreateAction from './CreateAction.vue';

export default {
  components: {
    EditTeimosinhaMessage,
    EditAction,
    CreateTeimosinhaMessage,
    CreateAction,
  },
  props: {
    inbox: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      teimosinhaEnabled: false,
      teimosinha_for_agents: false,
      teimosinha_for_clients: false,
      respondOpenConversations: false,
      numberOfMessages: 1,
      updateTeimosinhaStatusIsLoading: false,
      showDeleteConfirmationPopup: false,
      showEditMessagePopup: false,
      showEditActionPopup: false,
      showCreatePopup: false,
      showCreateActionPopup: false,
      selectedMessage: {},
      selectedMessageOrder: 1,
    };
  },
  computed: {
    firstMessagePossibleTimes() {
      return [...this.timeOptions];
    },
    timeOptions() {
      const label = i18n_key =>
        this.$t(`INBOX_MGMT.TEIMOSINHA.TIME_OPTIONS.${i18n_key}`);
      return [
        { key: 99999, name: label('DISABLED_LABEL'), disabled: true },
        { key: 300, name: label('5_MIN'), disabled: false },
        { key: 600, name: label('10_MIN'), disabled: false },
        { key: 1200, name: label('20_MIN'), disabled: false },
        { key: 1800, name: label('30_MIN'), disabled: false },
        { key: 3600, name: label('1_HR'), disabled: false },
        { key: 10800, name: label('3_HR'), disabled: false },
        { key: 21600, name: label('6_HR'), disabled: false },
        { key: 43200, name: label('12_HR'), disabled: false },
        { key: 82800, name: label('1_DAY'), disabled: false },
      ];
    },
    teimosinhaMessages() {
      const teimosinhaMessages = this.inbox?.teimosinha_messages;
      if (teimosinhaMessages && teimosinhaMessages?.length) {
        return teimosinhaMessages
          .filter(
            message => message.teimosinha_type === 'teimosinha_for_clients'
          )
          .sort((a, b) => a.id - b.id);
      }
      return [];
    },
    agentsTeimosinhaMessages() {
      const agentsTeimosinhaMessages = this.inbox?.teimosinha_messages;
      if (agentsTeimosinhaMessages && agentsTeimosinhaMessages?.length) {
        return agentsTeimosinhaMessages
          .filter(
            message => message.teimosinha_type === 'teimosinha_for_agents'
          )
          .sort((a, b) => a.id - b.id);
      }
      return [];
    },
    deleteMessage() {
      const text =
        this.$t('INBOX_MGMT.TEIMOSINHA.MESSAGE') +
        ' ' +
        this.selectedMessageOrder;
      return ` ${text}?`;
    },
  },
  mounted() {
    this.setInitialValue();
  },
  methods: {
    setInitialValue() {
      const teimosinha_enabled =
        this.inbox?.automatic_actions_settings?.teimosinha?.enabled;
      if (teimosinha_enabled !== undefined)
        this.teimosinhaEnabled = teimosinha_enabled;

      const respondOpenConversations =
        this.inbox?.automatic_actions_settings?.teimosinha
          ?.respond_open_conversations;
      if (respondOpenConversations)
        this.respondOpenConversations = respondOpenConversations;

      const teimosinha_for_agents =
        this.inbox?.automatic_actions_settings?.teimosinha
          ?.teimosinha_for_agents;
      if (teimosinha_for_agents)
        this.teimosinha_for_agents = teimosinha_for_agents;

      const teimosinha_for_clients =
        this.inbox?.automatic_actions_settings?.teimosinha
          ?.teimosinha_for_clients;
      if (teimosinha_for_clients)
        this.teimosinha_for_clients = teimosinha_for_clients;
    },
    async updateTeimosinhaOptions() {
      const payload = {
        id: this.inbox.id,
        teimosinhaConfig: {
          enabled: this.teimosinhaEnabled,
          respond_open_conversations: this.respondOpenConversations,
          teimosinha_for_agents: this.teimosinha_for_agents,
          teimosinha_for_clients: this.teimosinha_for_clients,
        },
      };
      try {
        this.updateTeimosinhaStatusIsLoading = true;
        await this.$store.dispatch(
          'inboxes/updateInboxTeimosinhaStatus',
          payload
        );
        this.updateTeimosinhaStatusIsLoading = false;

        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
        this.updateTeimosinhaStatusIsLoading = false;
      }
    },
    openEditPopup(message, message_order, type) {
      if (type === 'client') {
        this.showEditMessagePopup = true;
      } else if (type === 'agent') {
        this.showEditActionPopup = true;
      }
      this.selectedMessage = message;
      this.selectedMessageOrder = message_order;
    },
    openCreatePopupForAgents() {
      this.showCreateActionPopup = true;
    },
    openCreatePopupForClients() {
      this.showCreatePopup = true;
    },
    hideEditPopup() {
      this.showEditMessagePopup = false;
      this.showEditActionPopup = false;
    },
    hideCreatePopup() {
      this.showCreatePopup = false;
    },
    hideCreateActionPopup() {
      this.showCreateActionPopup = false;
    },
    openDeletePopup(message, message_order) {
      this.showDeleteConfirmationPopup = true;
      this.selectedMessage = message;
      this.selectedMessageOrder = message_order;
    },
    closeDeletePopup() {
      this.showDeleteConfirmationPopup = false;
    },
    async confirmDeletion() {
      const payload = {
        inboxId: this.inbox.id,
        messageId: this.selectedMessage.id,
      };
      try {
        await this.$store.dispatch('inboxes/destroyTeimosinhaMessage', payload);
        this.showDeleteConfirmationPopup = false;
        useAlert(
          this.$t('INBOX_MGMT.TEIMOSINHA.DELETE_MESSAGE.API.SUCCESS_MESSAGE')
        );
      } catch (error) {
        useAlert(
          this.$t('INBOX_MGMT.TEIMOSINHA.DELETE_MESSAGE.API.ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<template>
  <div>
    <label class="medium-9 columns settings-item">
      {{ $t('INBOX_MGMT.TEIMOSINHA.SELECT_HEADER') }}
      <select v-model="teimosinhaEnabled">
        <option :value="true">
          {{ $t('INBOX_MGMT.EDIT.AGENT_NAME_ON_MESSAGE.ENABLED') }}
        </option>
        <option :value="false">
          {{ $t('INBOX_MGMT.EDIT.AGENT_NAME_ON_MESSAGE.DISABLED') }}
        </option>
      </select>
      <woot-submit-button
        v-tooltip.top="
          teimosinhaEnabled &&
          !teimosinhaMessages.length &&
          !agentsTeimosinhaMessages.length
            ? $t('INBOX_MGMT.TEIMOSINHA.ONE_MESSAGE_WARNING')
            : ''
        "
        :button-text="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
        :loading="false"
        :disabled="
          teimosinhaEnabled &&
          !teimosinhaMessages.length &&
          !agentsTeimosinhaMessages.length
        "
        @click="updateTeimosinhaOptions"
      />
      <div v-if="teimosinhaEnabled" class="pt-3 pb-1">
        <div>
          <span class="font-bold text-base">
            {{ $t('INBOX_MGMT.TEIMOSINHA.ADDITIONAL_SETTINGS.HEADER') }}
          </span>
          <div class="flex items-center pl-2">
            <input
              id="teimosinha_for_clients"
              v-model="teimosinha_for_clients"
              class="!my-0 mr-2 ml-0"
              type="checkbox"
            />
            <label class="text-sm" for="teimosinha_for_clients">
              {{
                $t(
                  'INBOX_MGMT.TEIMOSINHA.ADDITIONAL_SETTINGS.TEIMOSINHA_FOR_CLIENTS'
                )
              }}
            </label>
          </div>
          <div class="flex items-center pl-2">
            <input
              id="teimosinha_for_agents"
              v-model="teimosinha_for_agents"
              class="!my-0 mr-2 ml-0"
              type="checkbox"
            />
            <label class="text-sm" for="teimosinha_for_agents">
              {{
                $t(
                  'INBOX_MGMT.TEIMOSINHA.ADDITIONAL_SETTINGS.TEIMOSINHA_FOR_AGENTS'
                )
              }}
            </label>
          </div>
        </div>
        <!-- Teimosinha for clients Messages list -->
        <div v-if="teimosinha_for_clients" class="pt-3">
          <span class="font-bold text-base">
            {{ $t('INBOX_MGMT.TEIMOSINHA.MESSAGES_LIST.HEADER_FOR_CLIENTS') }}
          </span>
          <div class="flex items-center pl-2">
            <input
              id="respondOpenConversations"
              v-model="respondOpenConversations"
              class="!my-0 mr-2 ml-0"
              type="checkbox"
            />
            <label class="text-sm" for="respondOpenConversations">
              {{
                $t(
                  'INBOX_MGMT.TEIMOSINHA.ADDITIONAL_SETTINGS.RESPOND_OPEN_CONVERSATIONS'
                )
              }}
            </label>
          </div>
          <br />
          <woot-button
            v-if="teimosinhaEnabled"
            variant="smooth"
            color-scheme="warning"
            icon="add-circle"
            @click="openCreatePopupForClients"
          >
            {{ $t('INBOX_MGMT.TEIMOSINHA.CREATE_MESSAGE.LABEL_FOR_CLIENTS') }}
          </woot-button>
          <br />
          <br />
          <table class="woot-table">
            <thead class="text-xs">
              <th>
                {{
                  $t(
                    'INBOX_MGMT.TEIMOSINHA.MESSAGES_LIST.TABLE_HEADER.MESSAGES_COUNT'
                  )
                }}
              </th>
              <th>
                {{
                  $t('INBOX_MGMT.TEIMOSINHA.MESSAGES_LIST.TABLE_HEADER.MESSAGE')
                }}
              </th>
              <th
                v-tooltip.top-end="
                  $t('INBOX_MGMT.TEIMOSINHA.EDIT_MESSAGE.PRIVATE_MESSAGE.INFO')
                "
              >
                {{
                  $t('INBOX_MGMT.TEIMOSINHA.MESSAGES_LIST.TABLE_HEADER.PRIVATE')
                }}
              </th>
              <th>
                {{
                  $t('INBOX_MGMT.TEIMOSINHA.MESSAGES_LIST.TABLE_HEADER.TIME')
                }}
              </th>
            </thead>
            <tbody>
              <tr
                v-for="(message, index) in teimosinhaMessages"
                :key="message.content"
              >
                <td>{{ index + 1 }}</td>
                <td class="label-title">
                  <span class="overflow-hidden whitespace-nowrap text-ellipsis">
                    {{ message.content }}
                  </span>
                </td>
                <td class="label-title">
                  {{ message.private_message ? '✅' : '⛔' }}
                </td>
                <td class="w-[2rem]">
                  {{
                    timeOptions.find(
                      e => e.key === message.time_to_send_seconds
                    ).name
                  }}
                </td>
                <td class="button-wrapper">
                  <woot-button
                    v-tooltip.top="$t('LABEL_MGMT.FORM.EDIT')"
                    variant="smooth"
                    size="tiny"
                    color-scheme="secondary"
                    class-names="grey-btn"
                    icon="edit"
                    @click="openEditPopup(message, index + 1, 'client')"
                  />
                  <woot-button
                    v-tooltip.top="$t('LABEL_MGMT.FORM.DELETE')"
                    variant="smooth"
                    color-scheme="alert"
                    size="tiny"
                    icon="dismiss-circle"
                    class-names="grey-btn"
                    @click="openDeletePopup(message, index + 1)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <hr
          v-if="teimosinha_for_clients && teimosinha_for_agents"
          class="my-4 border-gray-200"
        />
        <!-- Teimosinha for agents Messages list -->
        <div v-if="teimosinha_for_agents" class="pt-3">
          <span class="font-bold text-base">
            {{ $t('INBOX_MGMT.TEIMOSINHA.MESSAGES_LIST.HEADER_FOR_AGENTS') }}
          </span>
          <br />
          <br />
          <woot-button
            v-if="teimosinhaEnabled"
            variant="smooth"
            color-scheme="warning"
            icon="add-circle"
            @click="openCreatePopupForAgents"
          >
            {{ $t('INBOX_MGMT.TEIMOSINHA.CREATE_MESSAGE.LABEL_FOR_AGENTS') }}
          </woot-button>
          <br />
          <br />
          <table class="woot-table">
            <thead class="text-xs">
              <th>
                {{
                  $t(
                    'INBOX_MGMT.TEIMOSINHA.MESSAGES_LIST.TABLE_HEADER.MESSAGES_COUNT'
                  )
                }}
              </th>
              <th>
                {{
                  $t('INBOX_MGMT.TEIMOSINHA.MESSAGES_LIST.TABLE_HEADER.MESSAGE')
                }}
              </th>
              <th
                v-tooltip.top-end="
                  $t('INBOX_MGMT.TEIMOSINHA.EDIT_MESSAGE.PRIVATE_MESSAGE.INFO')
                "
              >
                {{
                  $t(
                    'INBOX_MGMT.TEIMOSINHA.MESSAGES_LIST.TABLE_HEADER.UNASSIGN'
                  )
                }}
              </th>
              <th>
                {{
                  $t('INBOX_MGMT.TEIMOSINHA.MESSAGES_LIST.TABLE_HEADER.TIME')
                }}
              </th>
            </thead>
            <tbody>
              <tr
                v-for="(message, index) in agentsTeimosinhaMessages"
                :key="message.content"
              >
                <td>{{ index + 1 }}</td>
                <td class="label-title">
                  <span class="overflow-hidden whitespace-nowrap text-ellipsis">
                    {{ message.content }}
                  </span>
                </td>
                <td class="label-title">
                  {{ message.unassign_conversation ? '✅' : '⛔' }}
                </td>
                <td class="w-[2rem]">
                  {{
                    timeOptions.find(
                      e => e.key === message.time_to_send_seconds
                    ).name
                  }}
                </td>
                <td class="button-wrapper">
                  <woot-button
                    v-tooltip.top="$t('LABEL_MGMT.FORM.EDIT')"
                    variant="smooth"
                    size="tiny"
                    color-scheme="secondary"
                    class-names="grey-btn"
                    icon="edit"
                    @click="openEditPopup(message, index + 1, 'agent')"
                  />
                  <woot-button
                    v-tooltip.top="$t('LABEL_MGMT.FORM.DELETE')"
                    variant="smooth"
                    color-scheme="alert"
                    size="tiny"
                    icon="dismiss-circle"
                    class-names="grey-btn"
                    @click="openDeletePopup(message, index + 1)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </label>

    <!-- Modal para edição de mensagens de clientes -->
    <woot-modal v-model:show="showEditMessagePopup" :on-close="hideEditPopup">
      <EditTeimosinhaMessage
        :message="selectedMessage"
        :message-order="selectedMessageOrder"
        :time-options="timeOptions"
        :inbox-id="inbox.id"
        @close="hideEditPopup"
      />
    </woot-modal>

    <!-- Modal para edição de ações de agentes -->
    <woot-modal v-model:show="showEditActionPopup" :on-close="hideEditPopup">
      <EditAction
        :message="selectedMessage"
        :message-order="selectedMessageOrder"
        :time-options="timeOptions"
        :inbox-id="inbox.id"
        @close="hideEditPopup"
      />
    </woot-modal>

    <woot-modal v-model:show="showCreatePopup" :on-close="hideCreatePopup">
      <CreateTeimosinhaMessage
        :time-options="timeOptions"
        :inbox-id="inbox.id"
        @close="hideCreatePopup"
      />
    </woot-modal>

    <woot-modal
      v-model:show="showCreateActionPopup"
      :on-close="hideCreateActionPopup"
    >
      <CreateAction
        :time-options="timeOptions"
        :inbox-id="inbox.id"
        @close="hideCreateActionPopup"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('LABEL_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('LABEL_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="$t('LABEL_MGMT.DELETE.CONFIRM.YES')"
      :reject-text="$t('LABEL_MGMT.DELETE.CONFIRM.NO')"
    />
  </div>
</template>

<style scoped lang="scss">
.label-color--container {
  @apply flex items-center;
}
.label-title {
  span {
    @apply w-60 inline-block;
  }
}
</style>
